import { useState } from 'react'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'

import KinerjaDropdown from '.'

import type { KinerjaKsUtamaDetail } from 'types/kinerja-ks'
import type { KinerjaSelection } from 'types/kinerja'

interface KinerjaDropdownGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'className'> {
  kinerja: KinerjaSelection[]
  content: Array<KinerjaKsUtamaDetail>
  onChange?: (value: KinerjaSelection[]) => void
  addButtonLabel?: string
  baseNumber?: string | number
  className?: string
  allowDeleteSingleValue?: boolean
  showDeleteConfirmation?: boolean
  showPoint?: boolean
  hideDetails?: boolean
  offset?: number
  defaultUnselectedTargetQty?: string
}

const EMPTY_VALUE: KinerjaSelection = {
  id: 0,
  kuantitas: 0,
}

export default function KinerjaDropdownGroup({
  kinerja,
  content,
  onChange = () => {},
  addButtonLabel = 'Tambah',
  className = '',
  baseNumber,
  allowDeleteSingleValue = false,
  showDeleteConfirmation = false,
  showPoint = false,
  hideDetails = false,
  offset = 0,
  defaultUnselectedTargetQty = '',
  ...props
}: KinerjaDropdownGroupProps) {
  const [values, setValues] = useState<KinerjaSelection[]>(
    kinerja.length > 0 ? kinerja : [EMPTY_VALUE]
  )

  const isDisabledAddButton = values.some((value) => !value.id)

  const isAllOptionsSelected = content
    .map((option) => option.id)
    .every((id) => {
      return values.map((value) => value.id).includes(id)
    })

  const removeValue = (index: number) => {
    const newValues = [...values]
    newValues.splice(index, 1)
    return newValues
  }

  const onDelete = (index: number) => {
    const newValues = removeValue(index)
    onChange(newValues)

    if (newValues.length === 0) {
      setValues([EMPTY_VALUE])
    } else {
      setValues(newValues)
    }
  }

  const addNewValue = () => {
    const newValues = [...values, EMPTY_VALUE]
    setValues(newValues)
  }

  const handleChange = (option: KinerjaSelection, index) => {
    const newValues = [...values]
    newValues[index] = option
    setValues(newValues)
    onChange(newValues)
  }

  return (
    <div className={clsx('flex flex-col gap-6', className)} {...props}>
      {values.map((value, index) => {
        const selectedOption = content.find(
          (content) => content.id === value.id
        )
        const remainingOptions = content.filter(
          (content) => !values.map((value) => value.id).includes(content.id)
        )

        return (
          <KinerjaDropdown
            key={`KinerjaDropdown-${index}`}
            data-testid={`KinerjaDropdown-${index}`}
            kinerjaNumber={
              !!baseNumber
                ? `${baseNumber}.${index + 1}`
                : `${offset + index + 1}`
            }
            options={[
              ...(selectedOption ? [selectedOption] : []),
              ...remainingOptions,
            ]}
            value={value}
            onDelete={() => onDelete(index)}
            onChange={(option) => handleChange(option, index)}
            disableDeleteButton={
              values.length === 1 &&
              (allowDeleteSingleValue ? values[0].id === 0 : true)
            }
            showPoint={showPoint}
            hideDetails={hideDetails}
            showDeleteConfirmation={showDeleteConfirmation}
            defaultUnselectedTargetQty={defaultUnselectedTargetQty}
          />
        )
      })}

      <div>
        <Button
          color="white"
          disabled={isAllOptionsSelected || isDisabledAddButton}
          onClick={addNewValue}
        >
          <Icon className="mr-1">add_circle</Icon>
          {addButtonLabel}
        </Button>
      </div>
    </div>
  )
}
